import React from 'react'
import { Button } from 'react-bootstrap'
import { PERIOD_MONTH, PERIOD_YEAR, PLANS } from '../../../constants/plans'
import CurrencyDropdown from 'components/CurrencyDropdown/CurrencyDropDown'
import { availableCurrencies } from 'constants/currency'
import styles from '../PaymentModal.module.css'

const getPriceLabel = (plan, period, discountPercent = 0, showMonthlyEquivalent = false, decimals = 0) => {
  const currency = plan.prices[period]?.symbol || '$'
  const value = plan.prices[period]?.value || 0
  const originalPrice = plan.prices[period] && `${currency}${value.toFixed(decimals)}`

  if (showMonthlyEquivalent && period === PERIOD_YEAR) {
    const yearlyAmount = parseFloat(value)
    const monthlyEquivalent = yearlyAmount / 12
    return `${currency}${monthlyEquivalent}`
  }

  if (discountPercent) {
    const amount = parseFloat(value)
    const discount = parseFloat(discountPercent)
    const final = (discount * amount) / 100
    const finalPrice = amount - final

    return (
      <>
        <span className={styles.strikethrough}>{originalPrice}</span>
        <span className="ps-1">
          {currency}
          {finalPrice.toFixed(2)}
        </span>
      </>
    )
  } else {
    return originalPrice
  }
}

const PaymentSummary = ({
  plan,
  period,
  onPeriodChange,
  isLoading,
  isError,
  showExtra,
  currentCurrency,
  basePrice,
  extraTeamMembers,
  extraTeamMembersCost,
  total,
  showCurrencyDropdown,
  onCurrencySelected,
  discountPercent,
  hideTotals = false,
  hideGuarantee = false,
  isIndia
}) => {
  return (
    <div className={`${isLoading || isError ? 'd-none' : 'd-block'} mb-4`}>
      <div className="px-0 pt-4 pb-2 price_button">
        {plan.prices.monthly && !isIndia && (
          <Button
            className={`w-100 mb-3 p-4 bold ${period === 'monthly' ? styles.priceButton_p : styles.priceButton}`}
            variant="light"
            onClick={() => onPeriodChange(PERIOD_MONTH)}
          >
            <div className="d-flex justify-content-between align-items-center">
              <h6 className={`${styles.priceTag} text-darker-blue font-16 mb-0`}>Monthly</h6>
              <div className={`${styles.priceAmount} text-700 font-32 text-darker-blue line-height-100`}>
                {getPriceLabel(plan, PERIOD_MONTH)}{' '}
                <span className={`text-darker-blue line-height-100 font-24`}>/mo</span>
              </div>
            </div>
          </Button>
        )}

        {plan.prices.yearly && (
          <Button
            className={`w-100 p-4 ${period === PERIOD_YEAR ? styles.priceButton_p : styles.priceButton}`}
            variant="light"
            onClick={() => onPeriodChange(PERIOD_YEAR)}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-start">
                <h6 className={`${styles.priceTag} text-darker-blue font-16 text-decoration`}>Annual</h6>
                {[PLANS.PRO.id, PLANS.PRO_U.id, PLANS.TINY.id].includes(plan.id) && (
                  <span className={`badge bg-success mt-1 ${styles.priceBadge}`}>2 months free</span>
                )}
                {isIndia && plan.id === PLANS.PRO_MAX.id && (
                  <span className={`badge bg-success mt-1 ${styles.priceBadge}`}>Up to 40% off</span>
                )}
              </div>
              <div className={`${styles.priceAmount} text-700 font-32 text-darker-blue line-height-100`}>
                <div>
                  {getPriceLabel(plan, PERIOD_YEAR, 0, true)}{' '}
                  <span className={`text-darker-blue line-height-100 font-24`}>/mo</span>
                </div>
                <span className={`text-500 text-grey-black font-14 line-height-19`}>
                  Billed at {getPriceLabel(plan, PERIOD_YEAR)} /yr
                </span>
              </div>
            </div>
          </Button>
        )}
      </div>

      {!hideTotals && (
        <>
          {showExtra ? (
            <div className={`p-2 mt-3 ${styles.totalContainer}`}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="text-dark-blue font-16">Base plan:</span>
                <span className="text-dark-blue font-16">
                  {currentCurrency}
                  {basePrice.toFixed(2)}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="text-dark-blue font-16">Additional team members ({extraTeamMembers}):</span>
                <span className="text-dark-blue font-16">
                  +{currentCurrency}
                  {extraTeamMembersCost.toFixed(2)}
                </span>
              </div>
              <div className={`d-flex justify-content-between align-items-center mt-2 pt-2 ${styles.borderTop}`}>
                <span className="text-dark-blue font-20 text-700 line-height-27 d-block">TOTAL</span>
                <div className="d-flex flex-row align-items-center">
                  <span className="text-dark-blue font-20 text-700 line-height-27 me-2">
                    {currentCurrency === '$' ? 'USD ' : ''}
                    {currentCurrency}
                    {total.toFixed(2)}
                  </span>
                  {showCurrencyDropdown && (
                    <CurrencyDropdown
                      selectedCurrency={currentCurrency}
                      onCurrencySelected={onCurrencySelected}
                      availableCurrencies={availableCurrencies}
                      showSymbol={false}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className={`d-flex justify-content-between align-items-center p-2 mt-3 ${styles.totalContainer}`}>
              <div>
                <span className={`text-dark-blue font-20 text-700 line-height-27 d-block`}>TOTAL</span>
              </div>
              <div className="d-flex flex-row align-items-center">
                <span className="text-dark-blue font-20 text-700 line-height-27 me-2">
                  {currentCurrency === '$' ? 'USD ' : ''}
                  {getPriceLabel(plan, period, discountPercent, false, 2)}
                </span>
                {showCurrencyDropdown && (
                  <CurrencyDropdown
                    selectedCurrency={currentCurrency}
                    onCurrencySelected={onCurrencySelected}
                    availableCurrencies={availableCurrencies}
                    showSymbol={false}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}

      {!hideGuarantee && (
        <div className="text-end me-2 mt-1">
          <a
            className="text-darker-blue font-12 line-height-16 text-500"
            target="_blank"
            href="https://helpdesk.tiiny.host/en/article/how-does-your-7-day-money-back-guarantee-work-ghtpu0/"
            rel="noreferrer"
          >
            <img alt="dollar" src="/assets/icons/currency-dollar.svg" className="mb-1" height={14} />
            &nbsp;7-day money-back guarantee
          </a>
        </div>
      )}
    </div>
  )
}

export default PaymentSummary
